import { t as translate } from 'i18n';
/** Pure query-data → ECharts options. Model options can decorate, never rebind data. */
import { buildChartData, buildPieData, formatValue, kpiValues, normalizeRows } from './widgetData';
import { formatAmount, formatShare, formatTick, unitOf, unitSymbol } from './units';
import { buildWorldMap } from './worldMap';
import { BAR_RADIUS, calloutMark, referenceLines, selectedTicks } from './chartParts';
import { isPartial, timeTick } from './trendCard';
import { CHART_TYPE, mix, tone } from 'theme/chartThemes';

// Fallback only: the console passes the active chart theme's series.
const PALETTE = ['#4361ee', '#e86129', '#7c5cff', '#00b894'];
const CARTESIAN = ['line', 'bar', 'stacked', 'area', 'stacked_area', 'combo', 'ranked_bar', 'share_bar'];
const AXIS_TYPES = ['category', 'value', 'time', 'log'];
const ANNOTATIONS = new Set(['markLine', 'markPoint', 'markArea']);
const SERIES_STYLES = ['itemStyle', 'lineStyle', 'areaStyle'];
const SERIES_STATES = ['emphasis', 'select', 'blur'];
// Past these, line markers only hide the line and sampling keeps the shape.
const DENSE_POINTS = 60;
// Room the value axis usually takes; the callout and ticks only need it roughly.
const AXIS_INSET = { left: 64, right: 16 };
const SAMPLED_POINTS = 200;
const BLOCKED = new Set(['__proto__', 'constructor', 'prototype', 'data', 'dataset', 'source', 'encode', 'dimensions', 'transform']);
const SERIES_BINDING = new Set([
  'datasetIndex', 'datasetId', 'seriesLayoutBy',
  'coordinateSystem', 'xAxisIndex', 'xAxisId', 'yAxisIndex', 'yAxisId', 'geoIndex', 'geoId', 'calendarIndex', 'calendarId',
  'parallelIndex', 'parallelId', 'polarIndex', 'polarId', 'singleAxisIndex', 'singleAxisId', 'radarIndex', 'radarId', 'id',
]);
const MAP_BINDING = ['map', 'nameProperty', 'nameMap', 'mapValueCalculation'];
const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const object = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const number = (value) => typeof value === 'number' && Number.isFinite(value);
const title = (column) => column?.title || column?.key || '';
const fail = (error = 'shape') => ({ option: null, error });
const axisOptions = (value) => Array.isArray(value) ? value : object(value) ? [value] : [];
const typeOf = (value) => axisOptions(value)[0]?.type;
const colour = (palette, index) => palette[index % palette.length];

/** Drop executable values, external image resources, prototype keys and data bindings. */
const seriesPath = (path) => path[0] === 'series' && (path.length === 1 || (path.length === 2 && typeof path[1] === 'number'));
const annotationPath = (path) => ANNOTATIONS.has(path[path.length - 1]) && seriesPath(path.slice(0, -1));

function clean(value, path = []) {
  const key = path.filter((part) => typeof part === 'string').at(-1) || '';
  if (value === null || typeof value === 'boolean' || number(value)) return value;
  if (typeof value === 'string') {
    if (/^(?:image:\/\/|javascript:|vbscript:|data:)|url\s*\(/i.test(value)) return undefined;
    if (/(?:image|icon|symbol|src|background)/i.test(key) && /^https?:\/\//i.test(value)) return undefined;
    return value;
  }
  if (Array.isArray(value)) return value.map((item, index) => clean(item, [...path, index]) ?? null);
  if (!object(value)) return undefined;
  const result = {};
  for (const [name, item] of Object.entries(value)) {
    if (BLOCKED.has(name) && !(name === 'data' && annotationPath(path))) continue;
    if (seriesPath(path) && SERIES_BINDING.has(name)) continue;
    const safe = clean(item, [...path, name]);
    if (safe !== undefined) result[name] = safe;
  }
  return result;
}

/** The chart theme owns series colours; annotations keep theirs for their meaning. */
function withoutSeriesColours(patch) {
  const { color, ...rest } = patch;
  for (const key of SERIES_STYLES) {
    if (!object(rest[key])) continue;
    const { color: fill, ...style } = rest[key];
    rest[key] = style;
  }
  for (const key of SERIES_STATES) if (object(rest[key])) rest[key] = withoutSeriesColours(rest[key]);
  return rest;
}

const withoutScaleColours = (scale) => {
  if (!object(scale) || !object(scale.inRange)) return scale;
  const { color, ...inRange } = scale.inRange;
  return { ...scale, inRange };
};

function merge(base, patch) {
  if (!object(patch)) return patch === undefined ? base : patch;
  const result = { ...(object(base) ? base : {}) };
  for (const [key, value] of Object.entries(patch)) {
    result[key] = object(value) ? merge(result[key], value) : value;
  }
  return result;
}

function groups(rows, key, column) {
  if (!key) return [{ name: '', rows }];
  const result = new Map();
  const labelCounts = new Map();
  for (const row of rows) {
    // Raw identities prevent formatting two different groups into one dataset.
    const identity = JSON.stringify([typeof row[key], row[key]]);
    if (!result.has(identity)) {
      const name = formatValue(row[key], column);
      result.set(identity, { name, value: row[key], rows: [] });
      labelCounts.set(name, (labelCounts.get(name) || 0) + 1);
    }
    result.get(identity).rows.push(row);
  }
  return [...result.values()].map((group) => ({ ...group, name: labelCounts.get(group.name) > 1 ? `${group.name} (${JSON.stringify(group.value) ?? 'undefined'})` : group.name }));
}

function categoryValues(rows, column, project = (value) => value) {
  const indices = new Map();
  const labels = [];
  const index = (value) => {
    const shown = project(value);
    const identity = JSON.stringify([typeof shown, shown]);
    if (!indices.has(identity)) { indices.set(identity, labels.length); labels.push(formatValue(shown, column)); }
    return indices.get(identity);
  };
  rows.forEach((row) => index(row[column.key]));
  return { labels, index };
}

function timeBucket(column) {
  if (column.type !== 'time' && column.kind !== 'time') return null;
  const suffix = /\.(day|hour)$/.exec(column.key);
  return {
    granularity: typeof column.granularity === 'string' ? column.granularity : suffix?.[1],
    member: suffix ? column.key.slice(0, -suffix[0].length) : null,
  };
}

/** Project clock hours only in a day × hour grid, never in an hourly timeline. */
function heatmapCategories(rows, column, otherColumn) {
  const part = object(column.calendar) && column.calendar.part;
  if (part === 'iso_weekday' || part === 'hour_of_day') {
    if (column.kind !== 'dimension' || column.type !== 'number') return null;
    const min = part === 'iso_weekday' ? 1 : 0;
    const count = part === 'iso_weekday' ? 7 : 24;
    if (rows.some((row) => !Number.isInteger(row[column.key]) || row[column.key] < min || row[column.key] >= min + count)) return null;
    const values = Array.from({ length: count }, (_, index) => min + index);
    const labels = part === 'iso_weekday' ? [translate('assistant.dashboardDateFieldMon'), translate('assistant.dashboardDateFieldTue'), translate('assistant.dashboardDateFieldWed'), translate('assistant.dashboardDateFieldThu'), translate('assistant.dashboardDateFieldFri'), translate('assistant.dashboardDateFieldSat'), translate('assistant.dashboardDateFieldSun')] : values.map((hour) => `${String(hour).padStart(2, '0')}:00`);
    // The semantic model has already extracted these coordinates in its
    // declared timezone. Keep numeric identities, without converting dates.
    return {
      values, labels, calendar: true, index: (value) => value - min,
      formatter: (value) => labels[Number(value) - min] ?? String(value),
    };
  }
  const bucket = timeBucket(column);
  const other = timeBucket(otherColumn);
  // Canonical Cube keys identify the underlying member. Metadata aliases may
  // not expose it; those still need an explicitly daily companion axis.
  const hourly = bucket?.granularity === 'hour' && other?.granularity === 'day'
    && (!bucket.member || !other.member || bucket.member === other.member);
  if (!hourly) return categoryValues(rows, column);
  // Read the bucket's own wall-clock hour, including offset/Z strings, without
  // converting it into the browser's timezone or rounding non-hourly values.
  const buckets = new Map();
  const hours = new Map();
  for (const row of rows) {
    const value = row[column.key];
    const match = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[T ]([01]\d|2[0-3]):00(?::00(?:\.0+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/.exec(value);
    if (!match) return null;
    const hour = `${match[1]}:00`;
    buckets.set(value, hour);
    if (!hours.has(hour)) hours.set(hour, row);
  }
  // Sort at most 24 clock categories, rather than sorting/rebinding query rows.
  const ordered = [...hours].sort(([a], [b]) => a.localeCompare(b)).map(([, row]) => row);
  return { ...categoryValues(ordered, column, (value) => buckets.get(value)), hourly: true };
}

function dimensionKey(chart, columns) {
  const rest = columns.filter((column) => column.kind !== 'measure' && column.key !== chart.x);
  const key = chart.series || (rest.length === 1 ? rest[0].key : null);
  if (rest.some((column) => column.key !== key)) return { error: 'shape' };
  return { key };
}

function measureName(unit, titles) {
  const suffix = unitSymbol(unit);
  const labels = [...new Set(titles)].map((label) => suffix && label.endsWith(`, ${suffix}`) ? label.slice(0, -suffix.length - 2) : label);
  return [labels.join(', '), suffix].filter(Boolean).join(', ');
}

function textStyle(theme, color = theme.muted) {
  return { color, fontFamily: theme.fontFamily, fontSize: theme.type.label, fontWeight: 400, textBorderWidth: 0, textShadowBlur: 0 };
}

const axisText = (theme) => ({ ...textStyle(theme), fontSize: theme.type.axis });

function axisStyle(theme) {
  return {
    axisLabel: axisText(theme), nameTextStyle: axisText(theme),
    axisTick: { show: false }, minorTick: { show: false },
    axisLine: { show: false, lineStyle: { color: theme.border } },
  };
}

/** Only a band or a hairline: the selected tick names the period itself. */
const axisPointer = (theme, type) => ({
  show: true, type, snap: true, z: 0, label: { show: false },
  lineStyle: { color: theme.border, width: 1, type: 'solid' }, shadowStyle: { color: theme.raised, opacity: 0.6 },
});

/** Short ticks for a time axis: the category keeps the full date as its identity. */
function timeTicks(rows, column) {
  if (column.type !== 'time' && column.kind !== 'time') return null;
  const ticks = new Map(rows.map((row) => [formatValue(row[column.key], column), timeTick(row[column.key], column.granularity)]));
  return (label) => ticks.get(label) ?? label;
}

/** An axis bound that always includes a line drawn beyond the data. */
const beyond = (target, passes) => (extent) => (passes(extent) ? target : null);

const gradient = (colour) => ({ type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: `${colour}40` }, { offset: 1, color: `${colour}00` }] });
// A segment this thin beside the tallest stack cannot show a corner, so the one
// beneath it rounds as well.
const THIN_END = 0.02;
const roundedEnd = (value, horizontal) => {
  if (horizontal) return value < 0 ? [BAR_RADIUS, 0, 0, BAR_RADIUS] : [0, BAR_RADIUS, BAR_RADIUS, 0];
  return value < 0 ? [0, 0, BAR_RADIUS, BAR_RADIUS] : [BAR_RADIUS, BAR_RADIUS, 0, 0];
};
const measured = (point) => (Array.isArray(point) ? point[1] : point);

/** The stacking `stackEnds` assumes: a model's own corners or stack settings stand for the whole chart. */
function defaultStacking(options, horizontal, measures) {
  const zoomsValues = (zoom) => object(zoom) && (own(zoom, horizontal ? 'xAxisIndex' : 'yAxisIndex') || own(zoom, horizontal ? 'xAxisId' : 'yAxisId') || zoom.orient === (horizontal ? 'horizontal' : 'vertical'));
  const legends = axisOptions(options.legend);
  return !axisOptions(options.series).some((series) => object(series) && (['stack', 'stackOrder', 'stackStrategy'].some((key) => own(series, key))
    || (object(series.itemStyle) && ['borderRadius', 'barBorderRadius'].some((key) => own(series.itemStyle, key)))))
    && !measures.some((axis) => object(axis) && (axis.inverse || axis.type === 'log'))
    && !axisOptions(options.dataZoom).some(zoomsValues)
    && legends.length < 2 && legends[0]?.selectedMode !== 'single';
}

/**
 * A stack reads as one bar: its outer ends are rounded, its joins stay flat. The
 * engine rounds a series, not a stack, and which segment ends one depends on the
 * row and on what the legend shows, so the corners are set point by point.
 */
function stackEnds(series, horizontal, shown) {
  const stacks = new Map();
  series.forEach((item, position) => {
    if (!shown(item.name)) return;
    item.data.forEach((value, index) => {
      if (!number(value) || value === 0) return;
      // Positives and negatives stack apart, each from the axis outwards.
      const key = `${index}|${value < 0}`;
      if (!stacks.has(key)) stacks.set(key, []);
      stacks.get(key).push({ point: `${position}|${index}`, size: Math.abs(value) });
    });
  });
  const totals = [...stacks.values()].map((segments) => segments.reduce((sum, { size }) => sum + size, 0));
  const room = Math.max(0, ...totals) * THIN_END;
  const outer = new Set();
  for (const segments of stacks.values()) {
    let reached = 0;
    for (const { point, size } of segments.reverse()) {
      outer.add(point);
      reached += size;
      if (reached >= room) break;
    }
  }
  return series.map((item, position) => ({
    id: item.id,
    data: item.data.map((value, index) => (outer.has(`${position}|${index}`) ? { value, itemStyle: { borderRadius: roundedEnd(value, horizontal) } } : value)),
  }));
}

/** The first draw's corners, and `stacks(shown)` for the legend's later states. */
function roundedStacks(option, horizontal) {
  const { series } = option;
  const selected = axisOptions(option.legend)[0]?.selected;
  const ends = stackEnds(series, horizontal, (name) => selected?.[name] !== false);
  return {
    option: { ...option, series: series.map((item, index) => ({ ...item, data: ends[index].data })) },
    stacks: (shown) => ({ series: stackEnds(series, horizontal, shown) }),
  };
}

function numericAxis(unit, titles, index, count, horizontal, theme) {
  // One scale's ticks carry their unit already; several need telling apart.
  const name = count > 1 ? measureName(unit, titles) : '';
  return {
    id: `measure-${index}`, type: 'value', name,
    ...(unit === 'currency' ? { minInterval: 1 } : {}),
    position: horizontal ? index === 0 ? 'bottom' : 'top' : index === 0 ? 'left' : 'right',
    offset: index > 1 ? (index - 1) * 58 : 0,
    // Horizontal bars reserve width for category names. Build a sparse value
    // scale first; hideOverlap remains a fallback for especially narrow cards.
    ...axisStyle(theme), splitNumber: horizontal ? 2 : 4,
    axisLabel: { ...axisText(theme), hideOverlap: true, formatter: (value) => formatTick(value, unit) },
    splitLine: { show: index === 0, lineStyle: { color: theme.border, width: 1, type: 'solid' } },
    minorSplitLine: { show: false },
  };
}

/**
 * A time series marks its latest whole period, a comparison its largest value,
 * unless told. Rows can arrive newest first, so `stamps` decide what is latest.
 */
function highlighted(values, stamps, partial, choice) {
  const known = values.map((value, index) => ({ value, index })).filter(({ value }) => number(value));
  if (!known.length) return -1;
  const whole = known.filter((item) => !partial[item.index]);
  const pool = whole.length ? whole : known;
  const newer = (item, best) => (stamps ? String(stamps[item.index]).localeCompare(String(stamps[best.index])) > 0 : item.index > best.index);
  if (choice === 'min') return pool.reduce((best, item) => (item.value < best.value ? item : best)).index;
  if (choice === 'last' || (stamps && choice !== 'max')) return pool.reduce((best, item) => (newer(item, best) ? item : best)).index;
  return pool.reduce((best, item) => (item.value > best.value ? item : best)).index;
}

function nativeAxesFit(options, xCount, yCount) {
  return axisOptions(options.xAxis).length <= xCount && axisOptions(options.yAxis).length <= yCount;
}

/** Each stack as shares of its own total; `amounts` keeps what it stands for. */
function asShares(data) {
  const amounts = data.datasets.map((dataset) => dataset.data);
  if (amounts.flat().some((value) => number(value) && value < 0)) return null;
  const totals = data.labels.map((_, index) => amounts.reduce((sum, values) => sum + (values[index] || 0), 0));
  const unit = data.datasets[0].unit;
  return {
    amounts, unit,
    datasets: data.datasets.map((dataset) => ({ ...dataset, unit: 'percent', data: dataset.data.map((value, index) => (number(value) && totals[index] > 0 ? (value / totals[index]) * 100 : null)) })),
    axes: data.axes.map((axis) => ({ ...axis, unit: 'percent' })),
  };
}

function cartesian(input, inputColumns, inputRows, palette, theme, options, meta) {
  const ranked = input.kind === 'ranked_bar';
  const share = input.kind === 'share_bar';
  const chart = { ...input, kind: ranked ? 'bar' : share ? 'stacked' : input.kind };
  if (share && meta.truncated) return fail();
  const yKey = chart.y[0];
  // Nulls rank last, and a ranking is read from its top whatever the query's order.
  const rows = ranked ? [...inputRows].sort((a, b) => (number(b[yKey]) ? b[yKey] : -Infinity) - (number(a[yKey]) ? a[yKey] : -Infinity)) : inputRows;
  // On an hourly axis each row is its own hour; other charts group by the day.
  const columns = inputColumns.map((column) => (column.granularity === 'hour' ? { ...column, hourly: true } : column));
  const byKey = Object.fromEntries(columns.map((column) => [column.key, column]));
  const requestedX = typeOf(options.xAxis);
  const requestedY = typeOf(options.yAxis);
  let horizontal = ranked || (requestedY === 'category' && (requestedX === 'value' || requestedX === 'log'));
  if (horizontal && !['bar', 'stacked'].includes(chart.kind)) return fail();
  const xType = horizontal ? 'category' : requestedX || 'category';
  if (!AXIS_TYPES.includes(xType) || (!horizontal && requestedY && !['value', 'log'].includes(requestedY))) return fail();
  const stacked = ['stacked', 'stacked_area'].includes(chart.kind);
  const allUnits = [...new Set(chart.y.map((key) => unitOf(byKey[key])))];
  if (stacked && allUnits.length > 1) return fail('units');
  let data;
  if (xType === 'category') {
    data = buildChartData({ ...chart, kind: stacked ? 'stacked' : chart.kind }, columns, rows, palette, { maxUnits: 3 });
    if (data.fallback) return fail(data.fallback);
  } else {
    const grouping = dimensionKey(chart, columns);
    if (grouping.error) return fail(grouping.error);
    const xColumn = byKey[chart.x];
    const coordinate = (row) => row[chart.x];
    if (rows.some((row) => xType === 'time'
      ? !(typeof coordinate(row) === 'string' || number(coordinate(row))) || !Number.isFinite(new Date(coordinate(row)).getTime())
      : !number(coordinate(row)) || (xType === 'log' && coordinate(row) <= 0))) return fail();
    const axes = allUnits.map((unit, index) => ({ id: index ? `y${index}` : 'y', unit, titles: chart.y.filter((key) => unitOf(byKey[key]) === unit).map((key) => title(byKey[key])) }));
    const datasets = [];
    for (const group of groups(rows, grouping.key, byKey[grouping.key])) {
      const seen = new Set();
      if (group.rows.some((row) => { const identity = xType === 'time' ? new Date(coordinate(row)).getTime() : coordinate(row); if (seen.has(identity)) return true; seen.add(identity); return false; })) return fail();
      for (const key of chart.y) datasets.push({
        label: grouping.key ? chart.y.length > 1 ? `${group.name} · ${title(byKey[key])}` : group.name : title(byKey[key]),
        data: group.rows.map((row) => [coordinate(row), number(row[key]) ? row[key] : null]),
        colour: colour(palette, datasets.length), axis: axes[allUnits.indexOf(unitOf(byKey[key]))].id, unit: unitOf(byKey[key]),
      });
    }
    data = { axes, datasets };
    // Numeric/time axes retain raw coordinates, including sub-day timestamps.
    if (!xColumn) return fail();
  }
  const xColumn = byKey[chart.x];
  // Axis appearance also expresses orientation: never move a supplied max,
  // formatter or rotation from the category axis onto the measurement axis.
  const explicitAxes = own(options, 'xAxis') || own(options, 'yAxis');
  if (chart.kind === 'bar' && !explicitAxes && allUnits.length === 1
    && !['time', 'measure'].includes(xColumn.kind) && !['time', 'number'].includes(xColumn.type)
    && !rows.some((row) => number(row[chart.x]))
    && data.labels?.length <= 12 && data.labels.some((label) => String(label).length > 18)) horizontal = true;
  if (!nativeAxesFit(options, horizontal ? data.axes.length : 1, horizontal ? 1 : data.axes.length)) return fail();
  const shares = share && data.labels && data.datasets.length > 1 ? asShares(data) : null;
  if (share && !shares) return fail();
  if (shares) data = { ...data, datasets: shares.datasets, axes: shares.axes };
  const tick = xType === 'category' ? timeTicks(rows, xColumn) : null;
  const selectable = xType === 'category' && !horizontal;
  const single = data.datasets.length === 1 && chart.kind !== 'combo';
  const lonely = selectable && single;
  const firstValues = data.datasets[0].data.map((value) => (Array.isArray(value) ? value[1] : value));
  const timed = xColumn.type === 'time' || xColumn.kind === 'time';
  const rawOf = timed && data.labels ? new Map(rows.map((row) => [formatValue(row[chart.x], xColumn), row[chart.x]])) : null;
  const stamps = !timed ? null : data.labels ? data.labels.map((label) => rawOf.get(label)) : data.datasets[0].data.map((point) => point[0]);
  const partial = timed && single ? stamps.map((value) => isPartial(value, xColumn.granularity, meta)) : [];
  const initial = lonely || (ranked && single) ? highlighted(firstValues, stamps, partial, chart.highlight) : -1;
  const view = { selected: initial, width: 0 };
  const accent = single && theme.named(chart.tone) ? theme.named(chart.tone) : tone(data.datasets[0].colour, theme);
  const ticks = () => selectedTicks({ ticks: (data.labels || []).map((label) => tick?.(label) ?? label), categories: data.labels, view, theme: { ...theme, inkMuted: theme.muted }, accent, wrap: !timed, ...AXIS_INSET });
  const mark = () => calloutMark({ theme, accent: firstValues[view.selected] < 0 && firstValues[view.selected] !== null ? theme.negative : accent, index: view.selected, count: firstValues.length, value: firstValues[view.selected], unit: data.datasets[0].unit, view, ...AXIS_INSET });
  const category = {
    type: xType, ...(data.labels ? { data: data.labels } : {}),
    ...(['value', 'log'].includes(xType) ? { name: unitSymbol(byKey[chart.x]), ...(unitOf(byKey[chart.x]) === 'currency' ? { minInterval: 1 } : {}) } : {}),
    ...axisStyle(theme), splitLine: { show: false },
    ...(horizontal ? { inverse: true } : {}),
    ...(selectable ? { axisPointer: axisPointer(theme, chart.kind === 'bar' || chart.kind === 'stacked' || chart.kind === 'combo' ? 'shadow' : 'line') } : {}),
    axisLabel: { ...axisText(theme), hideOverlap: true, ...(['value', 'log'].includes(xType) ? { formatter: (value) => formatTick(value, byKey[chart.x]) } : tick ? { formatter: tick } : {}),
      ...(horizontal ? { width: 132, overflow: 'break', interval: 0, lineHeight: 17, margin: 12 } : {}) },
  };
  if (selectable) category.axisLabel = { ...category.axisLabel, ...ticks() };
  const target = number(chart.target) ? chart.target : null;
  const axes = data.axes.map((axis, index) => {
    const built = numericAxis(axis.unit, axis.titles, index, data.axes.length, horizontal, theme);
    if (share) return { ...built, max: 100 };
    const quiet = ranked ? { ...built, axisLabel: { ...built.axisLabel, show: false }, splitLine: { show: false } } : built;
    return target !== null && index === 0 ? { ...quiet, max: beyond(target, ({ max }) => target > Math.max(max, 0)), min: beyond(target, ({ min }) => target < Math.min(min, 0)) } : quiet;
  });
  const requestedMeasures = axisOptions(horizontal ? options.xAxis : options.yAxis);
  const stackAlong = chart.kind === 'stacked' && xType === 'category' && defaultStacking(options, horizontal, requestedMeasures);
  if (requestedMeasures.some((axis) => axis?.type && !['value', 'log'].includes(axis.type))) return fail();
  const averageLabel = translate('assistant.trendCardAverageShort');
  const known = firstValues.filter((value, index) => number(value) && !partial[index]);
  const average = chart.average === true && single && !meta.truncated && known.length > 1 ? known.reduce((sum, value) => sum + value, 0) / known.length : null;
  const lines = [
    ...(average !== null ? [{ value: average, label: `${averageLabel} ${formatTick(average, data.datasets[0].unit)}` }] : []),
    ...(target !== null ? [{ value: target, label: typeof chart.target_label === 'string' && chart.target_label ? chart.target_label : translate('assistant.trendCardTarget'), colour: accent.colour }] : []),
  ];
  // Soft bars with one in the accent, as on a card; a hover moves the accent.
  const barFill = ({ dataIndex, value }) => {
    const fill = (Array.isArray(value) ? value[1] : value) < 0 ? theme.negative.colour : dataIndex === view.selected ? accent.colour : theme.neutralBar;
    return partial[dataIndex] ? mix(fill, theme.surface, dataIndex === view.selected ? 0.7 : 0.45) : fill;
  };
  const series = data.datasets.map((dataset, index) => {
    const axisIndex = data.axes.findIndex((axis) => axis.id === dataset.axis);
    const numericType = requestedMeasures[axisIndex]?.type || requestedMeasures[0]?.type || 'value';
    const values = dataset.data.map((value) => Array.isArray(value) ? value[1] : value);
    if (numericType === 'log' && values.some((value) => value !== null && value <= 0)) return null;
    // Without a marker a point between two gaps would disappear entirely.
    const gaps = values.includes(null);
    const dense = values.length > DENSE_POINTS && !gaps;
    // Grouped datasets repeat y in the same order for every category group.
    // A semantic combo must still mix bars and lines when options are omitted.
    const type = ['bar', 'stacked'].includes(chart.kind)
      || (chart.kind === 'combo' && index % chart.y.length === 0) ? 'bar' : 'line';
    const lone = type === 'bar' && single;
    const below = values.some((value) => value !== null && value < 0);
    const seriesColour = single ? accent.colour : dataset.colour;
    const fill = lone ? barFill : chart.kind === 'combo' && type === 'bar' ? mix(dataset.colour, theme.surface, 0.4) : dataset.colour;
    const barStyle = { color: fill, borderRadius: chart.kind === 'stacked' ? 0 : horizontal ? [0, BAR_RADIUS, BAR_RADIUS, 0] : [BAR_RADIUS, BAR_RADIUS, 0, 0], borderWidth: chart.kind === 'stacked' ? 1 : 0, borderColor: theme.surface };
    return {
      id: `bound-series-${index}`, name: dataset.label, type,
      ...(horizontal ? { xAxisIndex: axisIndex, yAxisIndex: 0 } : { xAxisIndex: 0, yAxisIndex: axisIndex }),
      data: below && type === 'bar' && !stacked ? dataset.data.map((value) => (number(measured(value)) ? { value, itemStyle: { borderRadius: roundedEnd(measured(value), horizontal) } } : value)) : dataset.data,
      itemStyle: type === 'bar' ? barStyle : { color: seriesColour, borderColor: theme.surface, borderWidth: 2 }, lineStyle: { color: seriesColour, width: 2 },
      label: textStyle(theme, theme.ink),
      ...(stacked ? { stack: 'bound-stack' } : {}),
      ...(['area', 'stacked_area'].includes(chart.kind) ? { areaStyle: { color: chart.kind === 'area' ? gradient(seriesColour) : seriesColour, opacity: chart.kind === 'area' ? 1 : 0.14 } } : {}),
      ...(type === 'bar' ? { barMaxWidth: 28, barCategoryGap: '42%', emphasis: { disabled: lone, focus: 'none' } } : {}),
      ...(lonely && initial >= 0 ? { markPoint: mark() } : {}),
      ...(index === 0 && lines.length ? { markLine: referenceLines(theme, lines, horizontal ? 'xAxis' : 'yAxis') } : {}),
      ...(lone && horizontal ? { label: { ...textStyle(theme, theme.ink), show: true, position: 'right', fontWeight: 600, formatter: ({ value }) => formatTick(Array.isArray(value) ? value[1] : value?.value ?? value, dataset.unit) } } : {}),
      ...(type === 'line' ? { connectNulls: false, symbol: 'circle', symbolSize: 7, showSymbol: gaps || values.length === 1, emphasis: { scale: false }, ...(dense && values.length > SAMPLED_POINTS ? { sampling: 'lttb' } : {}) } : {}),
    };
  });
  if (series.some((item) => item === null) || !series.some((item) => item.data.some((value) => number(measured(value?.value ?? value))))) return fail();
  const legendVisible = series.length > 1 || axisOptions(options.legend).some((legend) => legend?.show === true);
  const sliderVisible = axisOptions(options.dataZoom).some((zoom) => object(zoom) && (!zoom.type || zoom.type === 'slider') && zoom.show !== false && zoom.orient !== 'vertical');
  return {
    option: {
      legend: { show: series.length > 1 },
      grid: { left: 16, right: ranked ? 72 : horizontal ? 24 : 16 + Math.max(0, axes.length - 1) * 62, top: (legendVisible ? 42 : 24) + (horizontal ? Math.max(0, axes.length - 1) * 42 : 0), bottom: sliderVisible ? 64 : horizontal ? 36 : 24, containLabel: true },
      xAxis: horizontal ? axes : [category], yAxis: horizontal ? [category] : axes, series,
      ...(data.labels?.length > 30 ? { dataZoom: [{ type: 'inside', zoomOnMouseWheel: 'ctrl', moveOnMouseWheel: false }] } : {}),
    },
    units: data.datasets.map((dataset) => dataset.unit), trigger: 'axis', valueIndex: xType === 'category' ? null : 1,
    callout: single ? accent : null,
    ...(shares ? { shares: true, amounts: shares.amounts, amountUnit: shares.unit } : {}),
    ...(stackAlong ? { stackAlong: horizontal ? 'x' : 'y' } : {}),
    ...(partial.some(Boolean) && lonely ? { partial: chart.kind === 'bar' || chart.kind === 'stacked' ? 'bars' : 'points' } : {}),
    ...(selectable ? { selection: {
      view, count: firstValues.length, initial, keyboard: lonely, overrides: {}, holdsLayout: true, wraps: !timed,
      describe: (index) => `${tick?.(data.labels[index]) ?? data.labels[index]}: ${formatAmount(firstValues[index], data.datasets[0].unit)}`,
      patch() {
        const { axisLabel, ownCallout } = this.overrides;
        return {
          ...(view.grid ? { grid: view.grid } : {}),
          xAxis: [{ axisLabel: { ...ticks(), ...axisLabel } }],
          ...(lonely ? { series: [{ id: 'bound-series-0', ...(chart.kind === 'bar' || chart.kind === 'stacked' ? { itemStyle: { color: barFill } } : {}), ...(ownCallout ? {} : { markPoint: mark() }) }] } : {}),
        };
      },
    } } : {}),
  };
}

function slices(chart, columns, rows, palette, kind, theme) {
  // Pie legends and funnel stage lists render a DOM entry for every row.
  // Treemaps draw their labels inside the canvas and do not use those lists.
  const pie = buildPieData(chart, columns, rows, palette, kind === 'treemap' ? { maxSlices: Infinity } : undefined);
  if (pie.fallback) return fail(kind === 'pie' || kind === 'doughnut' ? 'pie' : 'shape');
  const items = pie.slices.map((slice, index) => ({ name: slice.label, value: slice.value, itemStyle: { color: colour(palette, index) } }));
  const label = { ...textStyle(theme, theme.ink), lineHeight: 17, overflow: 'break', backgroundColor: theme.surface, borderRadius: 4, padding: [3, 5] };
  if (kind === 'funnel') return { option: { legend: { show: false }, series: [{ id: 'bound-series-0', type: 'funnel', sort: 'none', top: 16, bottom: 16, left: '8%', width: '84%', gap: 4, data: items, itemStyle: { borderWidth: 0 }, label: { ...textStyle(theme, theme.ink), show: false, position: 'right' }, labelLine: { show: false }, emphasis: { label: { ...textStyle(theme, theme.ink), show: false }, itemStyle: { shadowBlur: 0 } } }] }, units: [pie.unit] };
  // Treemap labels fill their node's text box; a solid background would cover
  // the colour of the entire tile rather than just the category name.
  const treemapLabel = { ...label, backgroundColor: 'transparent', fontWeight: 600 };
  const tiles = items.map((item) => ({ ...item, label: { color: tone(item.itemStyle.color, theme).on } }));
  if (kind === 'treemap') return { option: { legend: { show: false }, series: [{ id: 'bound-series-0', type: 'treemap', top: 0, bottom: 0, left: 0, right: 0, roam: false, nodeClick: false, breadcrumb: { show: false }, data: tiles, itemStyle: { borderColor: theme.surface, borderWidth: 2, gapWidth: 2, borderRadius: 6 }, label: { ...treemapLabel, position: 'insideTopLeft', padding: [6, 8], formatter: '{b}' }, emphasis: { label: treemapLabel, itemStyle: { shadowBlur: 0 } } }] }, units: [pie.unit] };
  const itemStyle = { borderRadius: 6, borderWidth: 3, borderColor: theme.surface };
  const centre = kind === 'doughnut' ? { graphic: [
    { type: 'text', left: 'center', top: '42%', silent: true, style: { text: formatTick(pie.total, pie.unit), fill: theme.ink, fontFamily: theme.fontFamily, fontSize: theme.type.valueCompact, fontWeight: theme.type.valueWeight } },
    { type: 'text', left: 'center', top: '54%', silent: true, style: { text: pie.title, fill: theme.muted, fontFamily: theme.fontFamily, fontSize: theme.type.label, width: 110, overflow: 'truncate' } },
  ] } : {};
  return { option: { ...centre, legend: { show: false }, series: [{ id: 'bound-series-0', name: pie.title, type: 'pie', radius: kind === 'doughnut' ? ['60%', '84%'] : '84%', center: ['50%', '50%'], data: items, itemStyle, label: { ...textStyle(theme, theme.ink), show: false, formatter: '{b}' }, labelLine: { show: false }, emphasis: { scaleSize: 3, itemStyle: { shadowBlur: 0 } } }] }, units: [pie.unit], shares: true };
}

/** Steps floating from one running total to the next, then that total. */
function waterfall(chart, columns, inputRows, palette, theme, meta) {
  const byKey = Object.fromEntries(columns.map((column) => [column.key, column]));
  const xColumn = byKey[chart.x];
  const yColumn = byKey[chart.y[0]];
  const rest = columns.filter((column) => column.kind !== 'measure' && column.key !== chart.x);
  if (rest.length || meta.truncated || inputRows.some((row) => !number(row[yColumn.key]))) return fail();
  const timed = xColumn.type === 'time' || xColumn.kind === 'time';
  const rows = timed ? [...inputRows].sort((a, b) => String(a[chart.x]).localeCompare(String(b[chart.x]))) : inputRows;
  const short = timeTicks(rows, xColumn);
  const steps = rows.map((row) => row[yColumn.key]);
  const total = steps.reduce((sum, value) => sum + value, 0);
  const full = rows.map((row) => formatValue(row[chart.x], xColumn));
  if (new Set(full).size !== full.length) return fail();
  const totalLabel = translate('assistant.trendCardTotal');
  const categories = [...full, totalLabel];
  const accent = theme.named(chart.tone) || theme.accent;
  const spans = [];
  steps.reduce((start, value) => {
    spans.push([start, start + value]);
    return start + value;
  }, 0);
  spans.push([0, total]);
  // The total stands apart from the steps, also where the accent is ink itself.
  const totalColour = accent.colour === theme.ink ? palette[0] : theme.ink;
  const paint = (index) => {
    if (index === steps.length) return totalColour;
    return steps[index] < 0 ? theme.negative.colour : accent.colour;
  };
  const part = (index, value, radius) => (value === null ? null : { value, itemStyle: { color: paint(index), borderRadius: radius } });
  const base = spans.map(([from, to]) => (Math.min(from, to) >= 0 ? Math.min(from, to) : Math.max(from, to) <= 0 ? Math.max(from, to) : 0));
  const up = spans.map(([from, to], index) => (Math.max(from, to) <= 0 ? null : part(index, Math.max(from, to) - Math.max(Math.min(from, to), 0), [BAR_RADIUS, BAR_RADIUS, 0, 0])));
  const down = spans.map(([from, to], index) => (Math.min(from, to) >= 0 ? null : part(index, Math.min(from, to) - Math.min(Math.max(from, to), 0), [0, 0, BAR_RADIUS, BAR_RADIUS])));
  const values = [...steps, total];
  const initial = values.length - 1;
  const view = { selected: initial, width: 0 };
  const unit = unitOf(yColumn);
  const ticks = () => selectedTicks({ ticks: categories.map((label) => short?.(label) ?? label), categories, view, theme: { ...theme, inkMuted: theme.muted }, accent, wrap: !timed, ...AXIS_INSET });
  const mark = () => {
    const [from, to] = spans[view.selected];
    return calloutMark({ theme, accent: view.selected === steps.length ? tone(totalColour, theme) : values[view.selected] < 0 ? theme.negative : accent, index: view.selected, count: values.length, value: values[view.selected], at: Math.max(from, to), unit, view, ...AXIS_INSET });
  };
  const shared = { type: 'bar', stack: 'bound-stack', barMaxWidth: 36, barCategoryGap: '36%', emphasis: { disabled: true }, xAxisIndex: 0, yAxisIndex: 0 };
  return {
    option: {
      legend: { show: false },
      grid: { left: 16, right: 16, top: 24, bottom: 24, containLabel: true },
      xAxis: [{ type: 'category', data: categories, ...axisStyle(theme), splitLine: { show: false }, axisPointer: axisPointer(theme, 'shadow'), axisLabel: { ...axisText(theme), ...ticks() } }],
      yAxis: [numericAxis(unit, [title(yColumn)], 0, 1, false, theme)],
      series: [
        { ...shared, id: 'bound-base', name: '', silent: true, itemStyle: { color: 'transparent' }, data: base },
        { ...shared, id: 'bound-series-0', name: title(yColumn), data: up, markPoint: mark() },
        { ...shared, id: 'bound-series-1', name: title(yColumn), data: down },
      ],
    },
    units: [unit, unit, unit], trigger: 'axis', valueIndex: null, callout: accent, steps: { values, categories, unit },
    selection: {
      view, count: values.length, initial, keyboard: true, overrides: {}, holdsLayout: true, wraps: !timed,
      describe: (index) => `${categories[index]}: ${formatAmount(values[index], unit)}`,
      patch() {
        const { axisLabel, ownCallout } = this.overrides;
        return {
          ...(view.grid ? { grid: view.grid } : {}),
          xAxis: [{ axisLabel: { ...ticks(), ...axisLabel } }],
          ...(ownCallout ? {} : { series: [{ id: 'bound-series-0', markPoint: mark() }] }),
        };
      },
    },
  };
}

/** Fact as a bar over a plan tick: `y` is the fact, then the plan. */
function bullet(chart, columns, rows, palette, theme) {
  const byKey = Object.fromEntries(columns.map((column) => [column.key, column]));
  const [fact, plan] = chart.y.map((key) => byKey[key]);
  if (chart.y.length !== 2 || unitOf(fact) !== unitOf(plan)) return fail(chart.y.length === 2 ? 'units' : 'shape');
  const xColumn = byKey[chart.x];
  const rest = columns.filter((column) => column.kind !== 'measure' && column.key !== chart.x);
  if (rest.length || (!xColumn && rows.length !== 1) || rows.some((row) => !number(row[fact.key]) || !number(row[plan.key]))) return fail();
  const unit = unitOf(fact);
  const accent = theme.named(chart.tone) || theme.accent;
  const labels = rows.map((row) => (xColumn ? formatValue(row[chart.x], xColumn) : title(fact)));
  const facts = rows.map((row) => row[fact.key]);
  const plans = rows.map((row) => row[plan.key]);
  return {
    option: {
      legend: { show: true },
      grid: { left: 16, right: 96, top: 40, bottom: 8, containLabel: true },
      xAxis: [{ type: 'value', show: false, min: Math.min(0, ...facts, ...plans), ...axisStyle(theme), splitLine: { show: false } }],
      yAxis: [{ type: 'category', inverse: true, data: labels, ...axisStyle(theme), splitLine: { show: false }, axisLabel: { ...axisText(theme), width: 132, overflow: 'break', interval: 0 } }],
      series: [
        { id: 'bound-series-0', name: title(fact), type: 'bar', barWidth: 16, showBackground: true, backgroundStyle: { color: theme.raised, borderRadius: BAR_RADIUS }, itemStyle: { color: accent.colour, borderRadius: BAR_RADIUS }, emphasis: { disabled: true }, data: facts,
          label: { ...textStyle(theme, theme.ink), show: true, position: 'right', fontWeight: 600, formatter: ({ dataIndex }) => `${formatTick(facts[dataIndex], unit)} / ${formatTick(plans[dataIndex], unit)}` } },
        { id: 'bound-series-1', name: title(plan), type: 'scatter', symbol: 'rect', symbolSize: [3, 26], z: 5, itemStyle: { color: theme.ink }, data: plans.map((value, index) => [value, index]) },
      ],
    },
    units: [unit, unit], trigger: 'axis', valueIndex: 0,
  };
}

function pointChart(chart, columns, rows, palette, theme, options) {
  const byKey = Object.fromEntries(columns.map((column) => [column.key, column]));
  const xColumn = byKey[chart.x];
  const yColumn = byKey[chart.y[0]];
  const sizeColumn = chart.kind === 'bubble' ? byKey[chart.size] : null;
  if (chart.y.length !== 1 || (chart.kind === 'bubble' && !sizeColumn)) return fail();
  const xType = typeOf(options.xAxis) || (xColumn.kind === 'measure' || xColumn.type === 'number' ? 'value' : xColumn.type === 'time' ? 'time' : 'category');
  const yType = typeOf(options.yAxis) || 'value';
  if (!AXIS_TYPES.includes(xType) || !['value', 'log'].includes(yType) || !nativeAxesFit(options, 1, 1)) return fail();
  const categories = xType === 'category' ? categoryValues(rows, xColumn) : null;
  if (rows.some((row) => !number(row[yColumn.key]) || (yType === 'log' && row[yColumn.key] <= 0)
    || (sizeColumn && (!number(row[sizeColumn.key]) || row[sizeColumn.key] < 0))
    || (xType === 'time' && (!(typeof row[xColumn.key] === 'string' || number(row[xColumn.key])) || !Number.isFinite(new Date(row[xColumn.key]).getTime())))
    || (['value', 'log'].includes(xType) && (!number(row[xColumn.key]) || (xType === 'log' && row[xColumn.key] <= 0))))) return fail();
  const maxSize = sizeColumn ? rows.reduce((max, row) => Math.max(max, row[sizeColumn.key]), 0) : 1;
  const detailColumns = columns.filter((column) => column.kind !== 'measure' && column.key !== chart.x && column.key !== chart.series);
  const series = groups(rows, chart.series, byKey[chart.series]).map((group, index) => ({
    id: `bound-series-${index}`, type: 'scatter', name: group.name || title(yColumn),
    data: group.rows.map((row) => ({
      name: detailColumns.map((column) => `${title(column)}: ${formatValue(row[column.key], column)}`).join(' · ') || formatValue(row[chart.x], xColumn),
      value: [categories ? categories.index(row[chart.x]) : row[chart.x], row[yColumn.key], ...(sizeColumn ? [row[sizeColumn.key]] : [])],
    })),
    itemStyle: { color: colour(palette, index), opacity: 0.8, borderColor: theme.surface, borderWidth: 1 }, label: textStyle(theme, theme.ink),
    symbolSize: sizeColumn ? (value) => maxSize > 0 ? 8 + Math.sqrt(Math.max(0, value[2]) / maxSize) * 42 : 8 : 10,
  }));
  return {
    option: {
      grid: { left: 72, right: 28, top: 48, bottom: 52, containLabel: true },
      xAxis: [{
        type: xType, name: title(xColumn), nameLocation: 'middle', nameGap: 30,
        ...(categories ? { data: categories.labels } : {}),
        ...axisStyle(theme), splitLine: { show: false },
        axisLabel: { ...axisText(theme), ...(!categories && xType !== 'time' ? { formatter: (value) => formatTick(value, xColumn) } : {}) },
      }],
      yAxis: [{
        ...numericAxis(unitOf(yColumn), [title(yColumn)], 0, 1, false, theme),
        name: measureName(unitOf(yColumn), [title(yColumn)]), nameLocation: 'middle', nameGap: 50,
      }],
      series,
    },
    units: series.map(() => unitOf(yColumn)), valueIndex: 1, xColumn, sizeColumn, categories: categories?.labels,
  };
}

function heatmap(chart, columns, rows, palette, theme, options) {
  const byKey = Object.fromEntries(columns.map((column) => [column.key, column]));
  const xColumn = byKey[chart.x];
  const groupColumn = byKey[chart.series];
  const yColumn = byKey[chart.y[0]];
  if (!groupColumn || chart.y.length !== 1 || columns.some((column) => column.kind !== 'measure' && ![chart.x, chart.series].includes(column.key))
    || [typeOf(options.xAxis), typeOf(options.yAxis)].some((type) => type && type !== 'category') || !nativeAxesFit(options, 1, 1)) return fail();
  const x = heatmapCategories(rows, xColumn, groupColumn);
  const y = heatmapCategories(rows, groupColumn, xColumn);
  if (!x || !y) return fail();
  const seen = new Set();
  const data = rows.map((row) => {
    const a = x.index(row[chart.x]); const b = y.index(row[chart.series]); const value = row[yColumn.key];
    const key = `${a}:${b}`;
    if (seen.has(key) || !number(value)) return null;
    seen.add(key); return [a, b, value];
  });
  if (data.some((item) => item === null)) return fail();
  const bounds = data.reduce(([min, max], item) => [Math.min(min, item[2]), Math.max(max, item[2])], [Infinity, -Infinity]);
  const calendarGrid = x.calendar || y.calendar;
  const axis = (categories, vertical) => ({
    type: 'category', data: categories.values || categories.labels, ...axisStyle(theme),
    ...(vertical && (categories.hourly || categories.calendar) ? { inverse: true } : {}),
    axisLabel: {
      ...axisText(theme), hideOverlap: true,
      ...(categories.formatter ? { formatter: categories.formatter } : {}),
      ...(categories.calendar && (vertical || categories.values.length === 7) ? { interval: 0 } : {}),
    },
    splitArea: { show: false },
    // Only real cells carry a value/colour. Grid lines leave missing calendar
    // combinations visible as empty cells, rather than invented zeroes.
    ...(calendarGrid ? { splitLine: { show: true, interval: 0, lineStyle: { color: theme.border, width: 1 } } } : {}),
  });
  return {
    option: { legend: { show: false }, grid: { left: 16, right: 16, top: 16, bottom: 64, containLabel: true }, xAxis: [axis(x, false)], yAxis: [axis(y, true)], visualMap: { min: bounds[0], max: bounds[1], calculable: false, orient: 'horizontal', left: 'center', bottom: 0, itemWidth: 10, itemHeight: 160, textStyle: axisText(theme), text: [formatTick(bounds[1], yColumn), formatTick(bounds[0], yColumn)], inRange: { color: [mix(colour(palette, 0), theme.surface, 0.1), colour(palette, 0)] }, formatter: (value) => formatTick(value, yColumn) }, series: [{ id: 'bound-series-0', name: title(yColumn), type: 'heatmap', data, label: textStyle(theme, theme.ink), itemStyle: { borderColor: theme.surface, borderWidth: 2, borderRadius: 3 }, emphasis: { itemStyle: { borderColor: theme.ink, borderWidth: 1, shadowBlur: 0 } } }] },
    units: [unitOf(yColumn)], valueIndex: 2, heatmapLabels: { x: x.labels, y: y.labels },
  };
}

function gauge(chart, columns, rows, palette, theme) {
  const column = columns.find((item) => item.key === chart.y[0]);
  if (chart.y.length !== 1 || rows.length !== 1 || !number(rows[0][column.key])) return fail();
  const value = rows[0][column.key];
  // Keep the scale above the pivot and the exact value/name below it. Pixel
  // offsets preserve their separation even in the minimum-height card.
  return {
    option: {
      legend: { show: false },
      series: [{
        id: 'bound-series-0', type: 'gauge', radius: '86%', center: ['50%', '68%'], startAngle: 180, endAngle: 0,
        min: Math.min(0, value), max: Math.max(unitOf(column) === 'percent' ? 100 : 1, value), splitNumber: 1,
        axisLine: { roundCap: true, lineStyle: { width: 14, color: [[1, theme.raised]] } }, progress: { show: true, width: 14, roundCap: true },
        itemStyle: { color: colour(palette, 0) }, axisTick: { show: false }, splitLine: { show: false, length: 0, distance: 0 },
        axisLabel: { ...axisText(theme), distance: -18, formatter: (amount) => formatTick(amount) },
        pointer: { show: false, width: 4, length: '58%' },
        title: { ...textStyle(theme, theme.muted), width: 200, overflow: 'break', lineHeight: 17, offsetCenter: [0, 30] },
        detail: { ...textStyle(theme, theme.ink), fontWeight: theme.type.valueWeight, fontSize: theme.type.value, lineHeight: 36, padding: 0, width: 'auto', formatter: (amount) => formatAmount(amount, column), offsetCenter: [0, -18] },
        data: [{ name: title(column), value }],
      }],
    }, units: [unitOf(column)],
  };
}

function kpi(chart, columns, rows, theme) {
  const tiles = kpiValues(chart, columns, rows);
  if (tiles.fallback) return fail('kpi');
  return { option: { legend: { show: false }, graphic: tiles.flatMap((tile, index) => [
    { type: 'text', left: 24, top: 24 + index * 76, style: { text: tile.title, fill: theme.muted, fontFamily: theme.fontFamily, fontSize: 12, fontWeight: 400 } },
    { type: 'text', left: 24, top: 46 + index * 76, style: { text: tile.value, fill: theme.ink, fontFamily: theme.fontFamily, fontSize: 26, fontWeight: 600 } },
  ]) }, units: [] };
}

function tooltipFormatter(result) {
  return (input) => {
    const items = Array.isArray(input) ? input : [input];
    // A waterfall's series are pieces of a floating bar; only the step means anything.
    if (result.steps) {
      const { values, categories, unit } = result.steps;
      const index = items[0]?.dataIndex;
      return values[index] === undefined ? '' : `${categories[index]}: ${formatAmount(values[index], unit)}`;
    }
    // A lone series reads like a card's callout: the pill names the period.
    const header = result.trigger === 'axis' && !result.callout ? items[0]?.axisValueLabel : null;
    const lines = header == null ? [] : [String(header)];
    for (const item of items) {
      if (!item) continue;
      const value = item.value ?? item.data?.value ?? item.data;
      const unit = result.units[item.seriesIndex || 0] || '';
      if (result.heatmapLabels && Array.isArray(value)) {
        lines.push(`${result.heatmapLabels.x[value[0]]} · ${result.heatmapLabels.y[value[1]]}: ${formatAmount(value[2], unit)}`);
      } else if (result.xColumn && Array.isArray(value)) {
        if (item.name) lines.push(String(item.name));
        const x = result.categories ? result.categories[value[0]] : result.xColumn.type === 'time' ? String(value[0]) : formatAmount(value[0], result.xColumn);
        lines.push(`${title(result.xColumn)}: ${x}`, `${item.seriesName || ''}: ${formatAmount(value[1], unit)}`);
        if (result.sizeColumn) lines.push(`${title(result.sizeColumn)}: ${formatAmount(value[2], result.sizeColumn)}`);
      } else if (result.mapLabels) {
        const label = result.mapLabels[item.name] || item.name || '';
        lines.push(`${label}: ${number(value) ? formatAmount(value, unit) : translate('assistant.answerWidgetNoData')}`);
      } else {
        const amount = Array.isArray(value) ? value[result.valueIndex ?? value.length - 1] : value;
        const name = result.trigger === 'axis' ? item.seriesName : item.name || item.seriesName;
        const stood = result.amounts?.[item.seriesIndex]?.[item.dataIndex];
        lines.push(result.callout ? formatAmount(amount, unit) : `${name || ''}: ${formatAmount(amount, unit)}${result.shares && number(item.percent) ? ` (${formatShare(item.percent)})` : ''}${number(stood) ? ` · ${formatAmount(stood, result.amountUnit)}` : ''}`);
      }
    }
    // renderMode:richText draws these strings; no HTML is generated or interpreted.
    return lines.join('\n');
  };
}

function overlay(result, options, kind, theme) {
  const safe = clean(options) || {};
  const { series: appearance, xAxis, yAxis, tooltip, geo, color, visualMap, ...rest } = safe;
  const option = merge(result.option, { ...rest, ...(visualMap === undefined ? {} : { visualMap: Array.isArray(visualMap) ? visualMap.map(withoutScaleColours) : withoutScaleColours(visualMap) }) });
  if (kind !== 'choropleth' && geo !== undefined) option.geo = geo;
  for (const [key, patch] of [['xAxis', xAxis], ['yAxis', yAxis]]) {
    if (!result.option[key]) continue;
    option[key] = result.option[key].map((axis, index) => {
      const item = Array.isArray(patch) ? patch[index] : patch;
      if (!object(item)) return axis;
      // A common style may apply to every generated unit axis; its ID identifies
      // just the first one, so it must not create duplicate component IDs.
      if (!Array.isArray(patch) && index > 0) {
        const { id, ...style } = item;
        return merge(axis, style);
      }
      return merge(axis, item);
    });
  }
  if (result.option.series) option.series = result.option.series.map((series, index) => {
    // A waterfall's invisible base is no series of the model's: positions start
    // at the steps, and labels there would print pieces instead of steps.
    if (kind === 'waterfall' && series.id === 'bound-base') return series;
    const position = kind === 'waterfall' ? index - 1 : index;
    const supplied = Array.isArray(appearance) ? appearance[position] : appearance;
    // A world map has one trusted geography. Geo appearance decorates that
    // series instead of creating a separate, unbound geo component.
    const geoStyle = Array.isArray(geo) ? geo[0] : geo;
    const patch = kind === 'choropleth' ? merge(object(geoStyle) ? geoStyle : {}, supplied) : supplied;
    if (!object(patch)) return series;
    const { type, ...style } = withoutSeriesColours(patch);
    if (kind === 'choropleth') [...MAP_BINDING, ...SERIES_BINDING].forEach((key) => { delete style[key]; });
    if (kind === 'bubble') delete style.symbolSize;
    if (kind === 'waterfall') delete style.label;
    const { markPoint, markLine, ...plain } = style;
    const merged = merge(series, { ...plain, ...(kind === 'combo' && ['line', 'bar'].includes(type) ? { type } : {}) });
    // A model's annotation stands on its own: merged into the platform's callout
    // it would inherit that callout's text, so it replaces it, and lines add up.
    if (object(markPoint)) {
      merged.markPoint = markPoint;
      if (result.selection) result.selection.overrides.ownCallout = true;
    }
    if (object(markLine)) merged.markLine = { ...markLine, data: [...(series.markLine?.data || []), ...(Array.isArray(markLine.data) ? markLine.data : [])] };
    if (kind === 'funnel' && object(style.label) && style.label.show === true && !own(style.emphasis?.label || {}, 'show')) {
      merged.emphasis = merge(merged.emphasis, { label: { show: true } });
    }
    for (const annotation of ANNOTATIONS) {
      if (object(merged[annotation])) merged[annotation] = {
        ...merged[annotation],
        label: { ...textStyle(theme, theme.ink), ...(annotation === 'markLine' ? { position: 'insideEnd' } : {}), ...(object(merged[annotation].label) ? merged[annotation].label : {}) },
      };
    }
    return merged;
  });
  const firstAxis = Array.isArray(xAxis) ? xAxis[0] : xAxis;
  if (result.selection && object(firstAxis?.axisLabel)) result.selection.overrides.axisLabel = firstAxis.axisLabel;
  if (kind === 'choropleth') {
    option.series = option.series.map((series, index) => ({ ...series, ...Object.fromEntries(MAP_BINDING.map((key) => [key, result.option.series[index][key]])) }));
    const bindScale = (scale) => {
      const bound = { ...scale, dimension: 0, seriesIndex: 0 };
      if (bound.type !== 'piecewise' && number(bound.min) && bound.min === bound.max) {
        const colors = bound.inRange?.color;
        const color = Array.isArray(colors) ? colors.at(-1) : result.option.visualMap.pieces?.[0]?.color || result.option.color?.[0];
        return { ...bound, type: 'piecewise', text: undefined, itemHeight: 12,
          pieces: [{ value: bound.min, label: formatAmount(bound.min, result.units[0]), ...(color ? { color } : {}) }] };
      }
      return bound;
    };
    option.visualMap = Array.isArray(option.visualMap) ? option.visualMap.map(bindScale) : bindScale(option.visualMap);
  }
  if (kind === 'kpi') option.graphic = result.option.graphic;
  // An axis pointer asks for a tooltip even with none to show, and a rich one
  // then crashes measuring content it never set: the pointer must not ask.
  const contentless = tooltip?.show === false || tooltip?.showContent === false;
  const silentTooltip = contentless || (result.selection?.keyboard && tooltip?.show !== true);
  if (result.selection && silentTooltip && option.xAxis?.[0]?.axisPointer) option.xAxis[0] = { ...option.xAxis[0], axisPointer: { ...option.xAxis[0].axisPointer, triggerTooltip: false } };
  option.tooltip = {
    ...merge({ trigger: result.trigger || 'item', confine: true, backgroundColor: result.callout?.colour || theme.chrome, borderColor: result.callout?.colour || theme.chrome, borderWidth: 0, borderRadius: 6, padding: result.callout ? [5, 8] : [8, 10], shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0, textStyle: { ...textStyle(theme, result.callout?.on || theme.onChrome), lineHeight: 18, fontWeight: result.callout ? 600 : 400 } }, tooltip),
    // The callout already shows what a lone series' tooltip would. Without
    // triggerOn none the engine restores a hidden rich tooltip after every
    // redraw and crashes measuring content it never set.
    ...(result.selection && silentTooltip ? { ...(contentless ? {} : { show: false }), triggerOn: 'none' } : {}),
    renderMode: 'richText', formatter: typeof tooltip?.formatter === 'string' ? tooltip.formatter : tooltipFormatter(result),
  };
  option.animation = typeof safe.animation === 'boolean' ? safe.animation : false;
  option.aria = { ...merge({ enabled: true }, safe.aria), enabled: true };
  return option;
}

/** {option} or {option:null,error:shape|units|pie|kpi}; never mutates its inputs. */
export function buildEChartsOption(chart, columns, inputRows, inputPalette, inputTheme = {}, meta = {}) {
  if (!object(chart) || !Array.isArray(chart.y) || !chart.y.length || !Array.isArray(columns)) return fail();
  const validColumns = columns.filter((column) => object(column) && typeof column.key === 'string');
  const byKey = Object.fromEntries(validColumns.map((column) => [column.key, column]));
  if (validColumns.length !== columns.length || new Set(validColumns.map((column) => column.key)).size !== validColumns.length
    || validColumns.some((column) => column.title != null && typeof column.title !== 'string')
    || chart.y.some((key) => !own(byKey, key)) || new Set(chart.y).size !== chart.y.length
    || (chart.x != null && !own(byKey, chart.x)) || (chart.series != null && !own(byKey, chart.series)) || (chart.size != null && !own(byKey, chart.size))) return fail();
  if (!['kpi', 'gauge', 'bullet'].includes(chart.kind) && !byKey[chart.x]) return fail();
  const rows = normalizeRows(inputRows, validColumns);
  if (!rows.length) return fail(chart.kind === 'kpi' ? 'kpi' : 'shape');
  if (rows.some((row) => [chart.x, chart.series].some((key) => key != null && row[key] != null && typeof row[key] === 'object'))) return fail();
  const options = object(chart.options) ? chart.options : {};
  if ([...axisOptions(options.xAxis), ...axisOptions(options.yAxis)].some((axis) => object(axis) && own(axis, 'type') && !AXIS_TYPES.includes(axis.type))) return fail();
  const palette = Array.isArray(inputPalette) ? inputPalette.filter((item) => typeof item === 'string' && clean(item, ['backgroundColor']) !== undefined) : [];
  const colours = palette.length ? palette : PALETTE;
  const accents = inputTheme?.chart?.tones;
  const theme = {
    fontFamily: inputTheme?.fontFamily || "'Manrope', system-ui, sans-serif", type: inputTheme?.type || CHART_TYPE,
    ink: inputTheme?.ink || inputTheme?.textColor || '#17171a', muted: inputTheme?.inkMuted || '#5c5c55', border: inputTheme?.border || '#ebeae4',
    chrome: inputTheme?.chrome || '#17171a', onChrome: inputTheme?.onChrome || '#ffffff', surface: inputTheme?.surface || '#ffffff', raised: inputTheme?.raised || '#f1f1ec',
  };
  theme.accent = tone(/^#[0-9a-f]{6}$/i.test(colours[0]) ? colours[0] : PALETTE[0], theme);
  theme.negative = accents?.rose || theme.accent;
  theme.neutralBar = inputTheme?.chart?.neutral?.bar || theme.border;
  // A widget's tone, else the theme's own accent where it has one.
  theme.named = (name) => accents?.[name ?? inputTheme?.chart?.defaultTone];
  theme.chart = inputTheme?.chart;
  // A one-colour chart takes its accent from the widget's tone.
  const toned = ['gauge', 'heatmap', 'choropleth'].includes(chart.kind) && theme.named(chart.tone) ? [theme.named(chart.tone).colour, ...colours] : colours;
  if (chart.kind === 'combo' && axisOptions(options.series).some((series) => series?.type && !['line', 'bar'].includes(series.type))) return fail();
  let result;
  if (CARTESIAN.includes(chart.kind)) result = cartesian(chart, validColumns, rows, colours, theme, options, meta);
  else if (['pie', 'doughnut', 'funnel', 'treemap'].includes(chart.kind)) result = slices(chart, validColumns, rows, colours, chart.kind, theme);
  else if (['scatter', 'bubble'].includes(chart.kind)) result = pointChart(chart, validColumns, rows, colours, theme, options);
  else if (chart.kind === 'waterfall') result = waterfall(chart, validColumns, rows, colours, theme, meta);
  else if (chart.kind === 'bullet') result = bullet(chart, validColumns, rows, colours, theme);
  else if (chart.kind === 'heatmap') result = heatmap(chart, validColumns, rows, toned, theme, options);
  else if (chart.kind === 'gauge') result = gauge(chart, validColumns, rows, toned, theme);
  else if (chart.kind === 'choropleth') result = buildWorldMap(chart, validColumns, rows, toned, theme);
  else if (chart.kind === 'kpi') result = kpi(chart, validColumns, rows, theme);
  else return fail();
  if (result.error) return result;
  result.option = {
    color: colours, textStyle: textStyle(theme, theme.ink),
    ...result.option,
    legend: { type: 'scroll', top: 0, left: 0, right: 0, icon: 'circle', itemWidth: 8, itemHeight: 8, itemGap: 14, textStyle: textStyle(theme, theme.ink), pageTextStyle: textStyle(theme), pageIconSize: 10, pageIconColor: theme.muted, pageIconInactiveColor: theme.border, ...result.option.legend },
  };
  const option = overlay(result, options, chart.kind, theme);
  return { option, ...(result.stackAlong ? roundedStacks(option, result.stackAlong === 'x') : {}), ...(result.unmatched ? { unmatched: result.unmatched } : {}), ...(result.selection ? { selection: result.selection } : {}), ...(result.partial ? { partial: result.partial } : {}) };
}
